import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Select from '@databases/atlaskit/Select';
import EntryLink from '@databases/content-link/EntryLink';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils, useActionWithDependencies } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { AsyncOperationStatus } from '@databases/redux-types';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { type EntryWithContext } from '@databases/types/database';
import {
  DatabaseFieldType,
  type RawDatabaseValue,
  type DatabaseField,
} from '@databases/types/database';
import {
  type MultiChange,
  MultiValueChangeType,
  type ValueChange,
} from '@databases/utils/database/changesets';
import { loadDependencies } from '@databases/utils/database/field-types';
import {
  getDatabaseEntries,
  getDatabaseValue,
  getEntryName,
  getFirstField,
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus,
} from '@databases/utils/redux/databases/selectors';
import { generateId } from '@databases/utils/utils';
import React, { type ReactElement, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { getSelectDatabaseDraft } from '../../utils';
import {
  CustomValueContainer,
  baseSelectStyleConfig,
  isRenderedInSidePanelStyling,
} from '../popover-edit-common';

const EntryLinkOptionIconStyles = xcss({
  marginRight: 'space.150',
});

const EntryLinkWrapper = styled.span`
  width: 100%;

  & a {
    font-size: 14px;
    line-height: 16px;
    border: none;
    color: ${token('color.text')};
    width: 100%;

    .iconBefore {
      margin-right: ${token('space.100')};
    }
  }
`;

export interface EntryLinkSelectProps {
  value: RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>;
  field: DatabaseField<DatabaseFieldType.ENTRY_LINK>;
  autoFocus?: boolean;
  isRequired?: boolean;
  isLoading?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  onChange: (
    value: RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>,
    change: ValueChange | null
  ) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  isRenderedInSidePanel?: boolean;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
}

const validateEntry = (
  value: Array<EntryWithContext>,
  isRequired: boolean | undefined,
  validationMessage: string
): string | undefined => {
  if (isRequired && value.length === 0) {
    return validationMessage;
  }
};

const getOptionValue = (value: EntryWithContext) => (value.entry ? value.entry.id : '');

const dispatchClickOnParentElement = (element: HTMLElement): null => {
  const clickEvent = new Event('click', {
    bubbles: true,
  });

  if (element.parentElement?.dataset.dispatchClick) {
    element.dispatchEvent(clickEvent);
    return null;
  }

  return element.parentElement && dispatchClickOnParentElement(element.parentElement);
};

/**
 * Since the select options are rendering out an anchor tag within this select we have to manually dispatch a click
 * event outside the element that is rendering the hover card so the select component can detect the change.
 */
const handleEntryLinkClick = (event: React.MouseEvent) => {
  const target = event.target as HTMLElement;
  dispatchClickOnParentElement(target);
};

/**
 * Renders a select box that allows selecting one or multiple (depending on isMulti) specific database entries from the given database.
 */
export function EntryLinkSelect({
  value,
  field,
  autoFocus,
  isRequired,
  isInvalid,
  onFocus,
  onChange,
  onBlur,
  isRenderedInSidePanel,
  addDataSectionRef,
}: EntryLinkSelectProps): ReactElement {
  const { multi: isMulti, contentId: databaseContentId } = field.settings;

  const store = defaultStoreUtils.useStore();
  const intl = useIntl();
  const { formatMessage } = intl;

  // Attempt to load the field's database into the store.
  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      try {
        if (databaseContentId) {
          await dispatch(loadDatabaseByContentId(databaseContentId));
        }
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (err) {
        if (err instanceof Error) {
          let errorBody = '';
          const errorMessage = err.message;

          if (errorMessage.includes('NotFoundException')) {
            errorBody = formatMessage(messages['snackbar-error.entry-link-not-found'], {
              fieldName: field.name,
            });
          } else if (errorMessage.includes('PermissionException')) {
            errorBody = formatMessage(
              messages['snackbar-error.entry-link-restricted-permission'],
              {
                fieldName: field.name,
              }
            );
          } else {
            errorBody = formatMessage(messages['snackbar-error.entry-link-load-error'], {
              fieldName: field.name,
            });

            const error = new Error(
              `Could not load database with content id ${databaseContentId}: ${err}`
            );
            logError(error);
          }

          currentSnackbarState.setMessage(errorBody, {
            id: 'database-entry-link-value-edit',
            type: 'error',
            showCloseButton: true,
          });
        }

        return;
      }
    }, [databaseContentId, field.name, formatMessage, dispatch])
  );

  const [isPending, targetDatabaseDraft] = defaultStoreUtils.useShallowEqualSelector(
    (state) => {
      if (databaseContentId) {
        const targetDatabaseStatus = selectDatabaseByContentIdStatus(state, {
          contentId: databaseContentId,
        });
        const targetDatabase = selectDatabaseByContentIdOptional(state, {
          contentId: databaseContentId,
        });
        return [
          targetDatabaseStatus?.status === AsyncOperationStatus.PENDING,
          targetDatabase &&
            selectDatabaseDraftByContentId(state, {
              contentId: databaseContentId,
              includeDeleted: false,
            }),
        ] as const;
      } else {
        return [false, undefined];
      }
    }
  );

  const dependencies = useActionWithDependencies(
    useCallback(
      (helpers) => {
        // Load first field dependencies (because the picker item label is the string representation of the main field)
        const firstField = targetDatabaseDraft && getFirstField(targetDatabaseDraft);
        if (firstField) {
          for (const entry of getDatabaseEntries(targetDatabaseDraft)) {
            const value = getDatabaseValue(targetDatabaseDraft, firstField, entry);
            loadDependencies(
              {
                contentId: targetDatabaseDraft.contentId,
                field: firstField,
                entryId: entry.id,
                value,
              },
              {
                ...helpers,
                selectDatabase: getSelectDatabaseDraft(true),
                intl,
              },
              store.getState()
            );
          }
        }
      },
      [targetDatabaseDraft, intl, store]
    )
  );

  // The currently selected values
  const resolvedValue = useMemo(
    () =>
      targetDatabaseDraft
        ? value.flatMap((v: any): Array<EntryWithContext> => {
            if (targetDatabaseDraft.entriesById[v.entryId]) {
              const entry = targetDatabaseDraft.entriesById[v.entryId];
              return [{ database: targetDatabaseDraft, entry }];
            } else {
              return [];
            }
          })
        : [],
    [targetDatabaseDraft, value]
  );

  // The options available in the select
  const resolvedOptions = useMemo(
    (): Array<EntryWithContext> => [
      ...((targetDatabaseDraft &&
        getDatabaseEntries(targetDatabaseDraft).map((entry) => ({
          database: targetDatabaseDraft,
          entry,
        }))) ??
        []),
    ],
    [targetDatabaseDraft]
  );

  const handleChange = useCallback(
    (passedValue: EntryWithContext | null) => {
      if (!passedValue) {
        return;
      }

      const singleEntryLinkValue = {
        _id: generateId(),
        entryId: passedValue.entry.id,
        title: getEntryName(passedValue, store.getState()),
      };

      const entryLinkValues = [...resolvedValue, passedValue].map((value) => ({
        _id: value.entry.id,
        entryId: value.entry.id,
        title: getEntryName(value, store.getState()),
      }));

      const delta: MultiChange<DatabaseFieldType.ENTRY_LINK> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: singleEntryLinkValue,
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [singleEntryLinkValue],
          };

      onChange(entryLinkValues, {
        type: DatabaseFieldType.ENTRY_LINK,
        delta,
      });
    },
    [store, resolvedValue, isMulti, onChange]
  );

  const getOptionLabel = useCallback(
    (value: EntryWithContext) => {
      const entryName = getEntryName(value, store.getState());

      return entryName
        ? entryName
        : formatMessage(messages['unnamed-entities.unnamed-entry']);
    },
    [formatMessage, store]
  );

  const formatOptionLabel = useCallback(
    (value: EntryWithContext): React.ReactNode => {
      const title = getOptionLabel(value);
      const entryRef = {
        databaseSpaceKey: value.database?.space?.key,
        databaseId: value.database?.id,
        entryId: value.entry.id,
        title,
      };
      const databaseContentId = value.database?.contentId;

      return (
        <Inline testId="entry-link-option-container" xcss={EntryLinkOptionIconStyles}>
          {
            // We have to attach the data attributes to this wrapper element to detect clicks within the
            // hover card element and correctly dispatch the click event
          }
          <EntryLinkWrapper data-dispatch-click>
            <EntryLink
              entryRef={entryRef}
              noFocus={false}
              onClick={handleEntryLinkClick}
              onFocus={(e) => {
                e.preventDefault();
              }}
              contentId={databaseContentId}
              isReadonly={true}
              isHoverCard
            />
          </EntryLinkWrapper>
        </Inline>
      );
    },
    [getOptionLabel]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (!isRenderedInSidePanel) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onBlur, addDataSectionRef, isRenderedInSidePanel]
  );

  return (
    <ValidationStatus
      error={validateEntry(
        resolvedValue,
        isRequired,
        formatMessage(messages['entry-picker.validate-error-text'])
      )}
    >
      {(validationProps) => (
        <Select<EntryWithContext>
          data-testid="entry-link-select"
          classNamePrefix="entry-link-picker"
          value={resolvedValue}
          onChange={handleChange}
          isDisabled={isPending ?? !targetDatabaseDraft}
          isInvalid={validationProps.isInvalid || isInvalid}
          formatOptionLabel={formatOptionLabel}
          blurInputOnSelect={false}
          closeMenuOnSelect={false}
          getOptionLabel={getOptionLabel}
          options={resolvedOptions}
          getOptionValue={getOptionValue}
          onFocus={onFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          isLoading={isPending || dependencies.isDispatching}
          noOptionsMessage={() => formatMessage(messages['entry-link.no-options'])}
          placeholder={formatMessage(messages['select-dropdown.placeholder'])}
          controlShouldRenderValue={false}
          isSeamless={true}
          styles={
            isRenderedInSidePanel ? isRenderedInSidePanelStyling : baseSelectStyleConfig
          }
          hideSelectedOptions={true}
          {...(!isRenderedInSidePanel && {
            menuPortalTarget: null,
            defaultMenuIsOpen: true,
            menuIsOpen: true,
            components: {
              IndicatorsContainer: () => null,
              ValueContainer: CustomValueContainer,
            },
          })}
        />
      )}
    </ValidationStatus>
  );
}

export default EntryLinkSelect;
